import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useDebounce } from "../../../hooks/useDebounce";
import { useSelector } from "react-redux";
import { selectAccess } from "../../../store/access/repository/selector";
import { deleteAccess } from "../../../store/access/useCases/deleteAccess/action";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getAccessList } from "../../../store/access/useCases/getAccessList/action";
import { tokens } from "../../../locales/tokens";
import { removeAllAccess } from "../../../store/access/useCases/deleteAllAccess/action";

export const useEditAccess = (type: string) => {
    const { id } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const searchTextRef = useRef<HTMLInputElement | null>(null);
    const [pageAmount, setPageAmount] = useState(0);
    const [filters, setFilters] = useState({ page: 1, perPage: 30, searchText: '', sortBy: 'User' });
    const scrollRef = useRef<HTMLDivElement>(null);
    const { accessList, accessCount, accessDeleteError, isLoading, getAccessListError } = useSelector(selectAccess);
    const [deleteClick, setDeleteClick] = useState(false);
    const [isProccess, setProcess] = useState(false);

    useEffect(() => {
        if (id) {
            dispatch(getAccessList({ 
                content_id: Number(id), 
                content_type: type, 
                search: filters.searchText, 
                type: filters.sortBy, 
                page: filters.page, 
                perPage: filters.perPage 
            }));
            setProcess(true);
        }
    }, [filters]);

    useEffect(() => {
        if (accessCount) {
            setPageAmount(Math.ceil(accessCount / filters.perPage));
        }
    }, [accessCount]);

    useEffect(() => {
        if (!isLoading && isProccess) {
            if (getAccessListError) {
                toast.error(getAccessListError)
            };
            setTimeout(() => setProcess(false), 400);
        }
    }, [getAccessListError, isLoading]);

    useEffect(() => {
        if (!isLoading && deleteClick) {
            if (accessDeleteError) {
                toast.error(accessDeleteError)
            } else {
                toast.success(t(tokens.access.accessRemoved));
                dispatch(getAccessList({ 
                    content_id: Number(id), 
                    content_type: type, 
                    search: filters.searchText, 
                    type: filters.sortBy, 
                    page: 1, 
                    perPage: filters.perPage 
                }));
            }
            setDeleteClick(false);
        }
    }, [accessDeleteError, isLoading]);

    const deleteAllAccess = () => {
        dispatch(removeAllAccess({
            type: filters.sortBy,
            content_id: Number(id), 
            content_type: type
        }));
        setDeleteClick(true);
    };

    const handleSortBy = (sortBy: string): void => {
        setFilters((prevState) => ({ ...prevState, sortBy, page: 1 }));
        scrollTopList();
        setProcess(true);
    };

    const handleChangeSearch = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, sortBy: string) => {
        setSearch(e.target.value);
        debouncedHandleUserSeacrh(e.target.value, sortBy);
    }, []);

    const handleSearchTextChange = useCallback((event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
    }, []);

    const onSearch = (value: any, sortBy: string) => {
        const query = value ? value : '';
        setFilters((prevState) => ({ ...prevState, searchText: query, page: 1 }));
        scrollTopList();
        setProcess(true);
    };

    const { debouncedWrapper: debouncedHandleUserSeacrh } = useDebounce(onSearch, 800);

    const handleDelete = (accessId: number | string) => {
        const body: any = { 
            type: filters.sortBy,
            content_id: Number(id),
            content_type: type,
        };
        if (filters.sortBy === 'User') body.id = accessId;
        if (filters.sortBy !== 'User') body.guid = accessId;
        dispatch(deleteAccess(body));
        setDeleteClick(true);
    };

    const handlePageChange = useCallback((event: ChangeEvent<unknown>, page: number): void => {
        setFilters((prevState) => ({ ...prevState, page }));
        scrollTopList();
    }, [setFilters]);

    const scrollTopList = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        };
    };

    const [searchTitle, setSearchTitle] = useState('');

    useEffect(() => {
        const type = filters.sortBy;
        if (type === "User") {
            setSearchTitle(t(tokens.access.searchUsers));
        } else if (type === "Department") {
            setSearchTitle(t(tokens.access.serachDepartment));
        } else {
            setSearchTitle(t(tokens.access.searchPosition));
        }
    }, [filters.sortBy]);

    return {
        filters, searchTextRef, handleSortBy, handleChangeSearch, handleSearchTextChange, handleDelete, pageAmount, handlePageChange,
        scrollRef, accessList, searchTitle, isProccess, deleteAllAccess
    };
};